<template>
  <div class="outer-tier-container">
    <!-- Toolbar: actions when tiers exist -->
    <div
      v-if="tiers_settings.enabled && (showSaveButton || tiers.length > 0)"
      class="d-flex flex-row justify-space-between align-center flex-wrap mb-4"
      style="gap: 8px"
    >
      <div class="d-flex flex-wrap align-center" style="gap: 8px">
        <v-btn
          color="secondary"
          class="text-capitalize"
          depressed
          :loading="loading || tiersLoading"
          :disabled="!showSaveButton || !canSave"
          @click="saveChanges"
          >{{ translations.saveChanges[lang] }}</v-btn
        >
        <v-btn
          color="secondary"
          class="text-capitalize"
          :loading="loading || tiersLoading"
          depressed
          :disabled="!showResetButton"
          @click="resetChanges"
          >{{ translations.resetChanges[lang] }}</v-btn
        >
        <div class="wheel-number-container">
          <b-select
            noTopMargin
            v-model="currentTiersNumber"
            :items="tierNumberValues"
            :label="translations.tiersNumber[lang]"
            no-clear-icon
            style="max-width: 140px"
            @change="handleTierNumberChange"
          ></b-select>
        </div>
      </div>
      <v-btn
        color="error"
        class="text-capitalize"
        :loading="loading || tiersLoading"
        depressed
        :disabled="!tiers.length || !getPermission('point_packages.delete')"
        @click="deleteTiersAlert"
        >{{ translations.deleteTiers[lang] }}</v-btn
      >
    </div>

    <v-toolbar
      v-if="(!showSaveButton && tiers.length === 0) || tiers_settings.enabled === false"
      flat
      class="mb-4"
    >
      <v-btn
        color="secondary"
        class="text-capitalize px-5 mr-4"
        :loading="loading || tiersLoading"
        depressed
        :disabled="!tiers_settings.enabled || !getPermission('point_packages.create')"
        @click="createTiers"
        >{{ translations.createPointTiers[lang] }}</v-btn
      >
    </v-toolbar>

    <div v-if="tiers_settings.enabled === false">
      <v-alert color="orange" type="warning" dismissible dense outlined>
        {{ translations.enableTierCreationAlert[lang] }}</v-alert
      >
    </div>

    <div v-else-if="tierValues && tierValues.length > 1" class="tiers-layout">
      <div class="tiers-main-row">
        <!-- Left: Carousel wheel tier selector -->
        <v-col cols="12" md="4" class="pa-0 justify-center align-center d-flex">
          <div class="wheel-with-connectors">
            <div
              class="tier-connector-arrow tier-connector-arrow--left"
              aria-hidden="true"
              :style="{
                borderLeftColor: tierValues[sliderValue]
                  ? tierValues[sliderValue].color
                  : 'transparent',
                '--connector-border-color': getConnectorBorderColor(
                  tierValues[sliderValue] ? tierValues[sliderValue].color : null
                ),
              }"
            ></div>
            <div class="wheel-container">
              <!-- Up arrow -->

              <button
                class="wheel-arrow"
                :disabled="sliderValue === 0"
                style="margin-top: 4px"
                @click="wheelScroll(-1)"
              >
                <v-icon size="18">mdi-chevron-up</v-icon>
              </button>
              <div class="wheel-track-wrap">
                <!-- Scrollable list -->
                <div
                  class="wheel-track"
                  ref="wheelViewport"
                  @wheel.prevent="onWheel"
                  @scroll.passive="onScroll"
                >
                  <div class="wheel-pad"></div>
                  <div
                    v-for="(tier, i) in tierValues"
                    :key="`wheel-${i}-${tier.key}`"
                    class="wheel-item"
                    :class="{ 'is-selected': sliderValue === i, 'is-first': i === 0 }"
                    :style="{
                      color: getTextColor(tier.color),
                      zIndex: tierValues.length - i,
                      '--wheel-item-bg': tier.color,
                      '--wheel-item-border-color': getConnectorBorderColor(tier.color),
                    }"
                    @click="selectTier(i)"
                  >
                    <span class="wheel-item-name" :style="{ color: getTextColor(tier.color) }">
                      <v-icon v-if="!tier.title[lang]" size="24" color="red lighten-3"
                        >mdi-alert</v-icon
                      >
                      <template v-else>{{ tier.title[lang] }}</template>
                    </span>
                    <span
                      class="wheel-item-from"
                      :style="{ color: getTextColor(tier.color) + 'cc' }"
                      >{{ tier.from }}{{ i === tierValues.length - 1 ? '+' : '' }}</span
                    >
                    <div
                      v-if="i !== tierValues.length - 1"
                      class="wheel-item-end-shape"
                      :style="{
                        backgroundColor: tier.color,
                        '--wheel-item-border-color': getConnectorBorderColor(tier.color),
                      }"
                      aria-hidden="true"
                    ></div>
                  </div>
                  <div class="wheel-pad"></div>
                </div>

                <!-- Fade top / bottom -->
                <div class="wheel-fade wheel-fade--top"></div>
                <div class="wheel-fade wheel-fade--bottom"></div>
              </div>

              <!-- Down arrow -->
              <button
                class="wheel-arrow"
                :disabled="sliderValue === tierValues.length - 1"
                @click="wheelScroll(1)"
              >
                <v-icon size="18">mdi-chevron-down</v-icon>
              </button>
            </div>
            <div
              class="tier-connector-arrow tier-connector-arrow--right"
              aria-hidden="true"
              :style="{
                borderRightColor: tierValues[sliderValue]
                  ? tierValues[sliderValue].color
                  : 'transparent',
                '--connector-border-color': getConnectorBorderColor(
                  tierValues[sliderValue] ? tierValues[sliderValue].color : null
                ),
              }"
            ></div>
          </div>
        </v-col>

        <v-col cols="12" md="8" class="pa-0">
          <!-- Right: Inline tier form for the selected tier -->
          <div
            class="tier-form-panel"
            v-if="tierValues[sliderValue]"
            :style="{
              borderLeft: `4px solid ${tierValues[sliderValue].color}`,
            }"
          >
            <PointTierForm
              :key="sliderValue"
              :tier="tierValues[sliderValue]"
              :tier-index="sliderValue"
              :infinity-multiplier="infinityMultiplier"
              :tier-values="tierValues"
              ref="tierForm"
              @submit="submitTierForm"
              @from-blur="
                (payload) => adjustPreviousTierMaximumAmountAndValidate(payload.i, payload.tierData)
              "
            />
          </div>
        </v-col>
      </div>
    </div>

    <!-- Dialogs -->
    <v-dialog v-model="saveDialog" :max-width="$vuetify.breakpoint.smAndDown ? '90%' : '40%'">
      <PointTierAlertDialog
        @close="saveDialog = false"
        @submit="savePointTiers"
        :alert-text="this.translations.confirmSaveMsg[this.lang]"
      />
    </v-dialog>
    <v-dialog v-model="deleteDialog" :max-width="$vuetify.breakpoint.smAndDown ? '90%' : '40%'">
      <PointTierAlertDialog
        @submit="deleteAllTiers"
        @close="deleteDialog = false"
        :alert-text="this.translations.confirmDeleteTiers[this.lang]"
      />
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import translations from '@/utils/translations/loyaltyPanel/pointTitles';
import validators from '@/utils/validators/loyaltyPanel/pointTiers';
import PointTierForm from './PointTierForm';
import PointTierAlertDialog from './PointTierAlertDialog';
import {
  mdiInfinity,
  mdiPlusCircle,
  mdiMinusCircle,
  mdiPencilOutline,
  mdiAlert,
  mdiArrowUpBold,
  mdiArrowDownBold,
} from '@mdi/js';
import { cloneDeep, isEqual, debounce } from 'lodash';

export default {
  name: 'PointTiers',

  components: { PointTierForm, PointTierAlertDialog },
  mixins: [translations, validators],

  data() {
    return {
      icons: {
        mdiInfinity,
        mdiPlusCircle,
        mdiMinusCircle,
        mdiPencilOutline,
        mdiAlert,
        mdiArrowUpBold,
        mdiArrowDownBold,
      },
      titleLang: this.$route.params.lang,
      descLang: this.$route.params.lang,
      saveDialog: false,
      deleteDialog: false,
      tierPointChanged: false,
      savedTiers: [],
      tiersAreNewlyCreated: false,
      tiersAreDeleted: false,
      dialogPersistent: false,
      tierIndex: 0,
      updatingTiers: false,
      sliderValue: 0,
      maxTiersNumber: 100,
      currentTiersNumber: 0,
      tierValues: [],
      wheelItemHeight: 150,
      scrollTimer: null,
      tiersNumberChanging: false,
    };
  },
  async mounted() {
    await this.fetchPointTitles();
    this.$nextTick(() => {
      this.$set(this, 'savedTiers', JSON.parse(JSON.stringify(this.tierValues)));
      this.tierPointChanged = false;
      this.initializeWheel();
    });

    this._applyTierNumberChange = debounce(() => {
      this.tiersNumberChanging = false;
    }, 150);
  },
  computed: {
    ...mapState('loyaltyPanel/pointTitles', ['tiersLoading', 'tierValid']),
    ...mapState('loyaltyPanel', ['tiers_settings']),
    ...mapState(['loading', 'errorMessage']),
    infinityMultiplier() {
      return (this.tiersData?.length && Math.max(1 + 1 / this.tiersData.length, 1.1)) || 2;
    },

    tiers: {
      get() {
        return this.$store.state.loyaltyPanel.pointTitles.tiers;
      },
      set(val) {
        this.setTiers(val);
      },
    },

    lang() {
      return this.$route.params.lang;
    },
    storeMode() {
      return this.$store.state.store?.display_mode || 'points';
    },
    title() {
      return this.mode === 1
        ? this.translations.addPointPackage[this.lang]
        : this.translations.updatePointPackage[this.lang];
    },

    pointTitle: {
      get() {
        return this.$store.state.loyaltyPanel.pointTitles.pointTitle;
      },
      set(val) {
        this.setPointTitle(val);
      },
    },
    tierNumberValues() {
      let numbers = [];
      for (let i = 2; i <= this.maxTiersNumber; i++) {
        numbers.push(i);
      }

      return numbers;
    },

    showSaveButton() {
      return this.tiersValid && !isEqual(this.tierValues, this.savedTiers);
    },
    canSave() {
      try {
        const hasUpdate = this.$store.getters.getPermission('point_packages.update');
        const hasCreate = this.$store.getters.getPermission('point_packages.create');
        return hasUpdate || (this.tiersAreNewlyCreated && hasCreate);
      } catch (e) {
        return false;
      }
    },
    showResetButton() {
      return !isEqual(this.tierValues, this.savedTiers);
    },
  },

  methods: {
    ...mapActions('loyaltyPanel/pointTitles', ['create', 'update', 'fetchPointTitles', 'remove']),
    ...mapMutations('loyaltyPanel/pointTitles', ['setTiers']),
    ...mapMutations(['setNotification']),
    ...mapActions(['getPermission']),

    removeTier() {
      const newTiers = cloneDeep(this.tierValues || []);
      if (!newTiers.length) return;
      newTiers.pop();
      if (newTiers.length === 1) newTiers.pop();
      this.tierValues = newTiers;
      this.tierPointChanged = true;
      this.$nextTick(() => {
        this.getTiersErrors();
        this.rerenderDOM();
      });
    },
    deleteTiersAlert() {
      this.deleteDialog = true;
    },
    resetChanges() {
      if (this.tiersAreNewlyCreated) {
        this.setTiers([]);
        this.$set(this, 'savedTiers', []);
        this.$set(this, 'tierValues', []);
        this.tiersAreNewlyCreated = false;
        this.tiersAreDeleted = false;
        this.tierPointChanged = false;
        this.resetValidation();
        this.$nextTick(() => {
          this.rerenderDOM();
          this.$refs?.tierForm?.resetForm();
        });
        return;
      }

      this.tierValues = cloneDeep(this.savedTiers);
      this.resetValidation();
      this.$nextTick(() => {
        this.$refs?.tierForm?.resetForm();
      });
    },

    transformValuesToTiers(values) {
      let tiersData = cloneDeep(values);
      // Remove fields from tiersData that are not present in this.tiers
      const baseLength = Math.min(this.tiers?.length || 0, tiersData?.length || 0);
      for (let idx = 0; idx < baseLength; idx++) {
        const tier = this.tiers[idx] || {};
        const allowedKeys = Object.keys(tier);
        const item = tiersData[idx];
        if (!item || typeof item !== 'object') continue;
        Object.keys(item).forEach((key) => {
          if (!allowedKeys.includes(key)) {
            delete item[key];
          }
        });
      }
      if (tiersData?.[0]?.['expiration_days']) {
        tiersData[0]['expiration_days'] = undefined;
      }

      if (Array.isArray(tiersData)) {
        tiersData.forEach((item) => {
          if (!item || typeof item !== 'object') return;
          if (item.from !== undefined && item.from !== null && item.from !== '') {
            const n = Number(item.from);
            item.from = Number.isNaN(n) ? 0 : n;
          }
          if (item.to !== undefined && item.to !== null && item.to !== '') {
            const m = Number(item.to);
            item.to = Number.isNaN(m) ? undefined : m;
          } else {
            item.to = item.to === '' ? undefined : item.to;
          }
        });
      }
      return tiersData;
    },

    getTierValues() {
      let vals = this.tiers?.map((e, i, a) => {
        if (a.length > 0) {
          let from = typeof e.from === 'number' ? e.from : 0;
          let to = typeof e.to === 'number' ? e.to : from + 10000;

          if (i === a.length - 1) {
            if (!e.to) e.to = parseInt(((from * this.infinityMultiplier) / 100) * 100);
          }
          let key = e.title[this.lang] + e.color + (e.from || 0) + (e.to || 0);

          e.expiration_days =
            (this.tiers_settings.system !== 'expiration_mode' && undefined) || e.expiration_days;
          key += e.expiration_days;
          let lastLimit = a[a.length - 1].to;
          let tierPoints = to - from;
          let width = (lastLimit && `${Math.round((tierPoints * 100) / lastLimit)}%`) || '50%';

          return {
            ...e,
            from,
            to,
            key,
            tierPoints,
            lastLimit,
            width,
          };
        }
        return e;
      });
      this.$set(this, 'tierValues', vals);
      return vals;
    },

    getTiersErrors(enforceNeighbors = false) {
      for (let [i, tier] of this.tierValues.entries()) {
        this.validateTierForm(i, tier);
        this.validateMinimumAmount(i, tier, enforceNeighbors);
        const tierErrors = [];
        Object.values(this.error).forEach((errorArr) => {
          if (
            Array.isArray(errorArr) &&
            errorArr[i] &&
            typeof errorArr[i] === 'string' &&
            errorArr[i].trim()
          ) {
            tierErrors.push(errorArr[i]);
          }
        });
        this.$set(this.tierValues[i], 'errors', tierErrors);
      }
    },

    async saveChanges() {
      this.getTiersErrors(true);
      let tiersData = this.transformValuesToTiers(this.tierValues);
      this.setTiers(tiersData);
      if (this.tierPointChanged) {
        return (this.saveDialog = true);
      }
      let res = {};
      if (this.tiersAreNewlyCreated) {
        res = await this.create();
      } else {
        res = await this.update();
      }
      if (res && !res.error) {
        await this.fetchPointTitles();
        this.getTierValues();
        this.$nextTick(() => {
          this.$set(this, 'savedTiers', JSON.parse(JSON.stringify(this.tierValues)));
          this.$set(this, 'tierValues', JSON.parse(JSON.stringify(this.tierValues)));
          this.tiersAreNewlyCreated = false;
          this.tierPointChanged = false;
        });
      }
    },

    async savePointTiers() {
      if (this.tiers?.[0]?.['expiration_days']) {
        this.tiers[0]['expiration_days'] = undefined;
      }

      const tiersData = this.transformValuesToTiers(this.tierValues || []);
      this.setTiers(tiersData);

      let res = {};
      if (this.tiersAreNewlyCreated) {
        res = await this.create();
      } else {
        res = await this.update();
      }
      this.saveDialog = false;
      if (res && !res.error) {
        await this.fetchPointTitles();
        this.getTierValues();
        this.$nextTick(() => {
          this.$set(this, 'savedTiers', JSON.parse(JSON.stringify(this.tierValues)));
          this.$set(this, 'tierValues', JSON.parse(JSON.stringify(this.tierValues)));
          this.tiersAreNewlyCreated = false;

          this.tierPointChanged = false;
        });
      } else if (
        res &&
        res.message ==
          'System currently maps users to newly updated packages. Please try again later.'
      ) {
        let errorText = {
          en: res.message,
          el: 'Το σύστημα αντιστοιχεί προς το παρόν τους χρήστες σε πρόσφατα ενημερωμένα πακέτα. Προσπαθήστε ξανά αργότερα.',
          it: 'Il sistema attualmente mappa gli utenti verso i pacchetti appena aggiornati. Riprovare più tardi.',
        };
        this.setNotification({
          show: true,
          type: 'error',
          text: errorText[this.lang],
        });
      } else if (res && res.message.includes('expiration days has to be greater than zero')) {
        let errorText = {
          en: 'Expiration days has to be greater than zero (0) in expiration mode',
          el: 'Οι ημέρες λήξεως πρέπει να είναι μεγαλύτερες από το μηδέν (0) στη λειτουργία με χρόνο λήξης.',
          it: 'I giorni di scadenza devono essere maggiori di zero (0) in modalità di scadenza.',
        };
        this.setNotification({
          show: true,
          type: 'error',
          text: errorText[this.lang],
        });
      }
    },

    async deleteAllTiers() {
      const res = await this.remove();
      this.deleteDialog = false;

      if (!res || !res.error) {
        this.savedTiers = [];
        this.$set(this, 'tierValues', []);
        this.tiersAreNewlyCreated = true;
        this.tiersAreDeleted = true;
        this.tierPointChanged = false;
        this.$nextTick(() => {
          this.rerenderDOM();
        });
      }
    },

    createTiers() {
      const step = 10000;
      this.setTiers([
        {
          title: { en: '', el: '', it: '' },
          description: { en: '', el: '', it: '' },
          color: '#' + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0'),
          from: 0,
          to: step - 1,
        },
        {
          title: { en: '', el: '', it: '' },
          description: { en: '', el: '', it: '' },
          from: step,
          to: step * 2 - 1,
          expiration_days: this.tiers_settings.system === 'expiration_mode' ? -1 : undefined,
          color: '#' + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0'),
        },
      ]);

      this.$nextTick(() => {
        this.getTierValues();
        this.sliderValue = 0;
        this.currentTiersNumber = this.tierValues ? this.tierValues.length : 0;
        this.selectTier(0);
        this.initializeWheel();
        this.rerenderDOM();
      });

      if (!this.savedTiers.length || this.tiersAreDeleted) {
        this.tiersAreNewlyCreated = true;
      }
    },

    addTier() {
      let newTier = {
        title: {
          en: '',
          el: '',
          it: '',
        },
        description: {
          en: '',
          el: '',
          it: '',
        },
        color: '#' + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0'),
        from: 0,
        to: 10000,
        expiration_days: this.tiers_settings.system === 'expiration_mode' ? -1 : undefined,
      };
      const step = 10000;
      if (this.tierValues && this.tierValues.length) {
        const newTiers = cloneDeep(this.tierValues || []);
        const last = newTiers[newTiers.length - 1];
        const lastTo = Number.isFinite(Number(last.to)) ? Number(last.to) : Number(last.from || 0);

        let candidateFrom = Math.ceil((lastTo + 1) / step) * step;

        if (!Number.isFinite(lastTo) || candidateFrom <= lastTo) {
          candidateFrom = lastTo + 1;
        } else {
          newTiers[newTiers.length - 1].to = candidateFrom - 1;
        }

        newTier.from = candidateFrom;
        newTier.to = parseInt((newTier.from * this.infinityMultiplier) / 100) * 100;
        newTiers.push(newTier);
        this.tierValues = newTiers;
      } else {
        // No existing tiers — create two nicely rounded tiers
        const first = cloneDeep(newTier);
        first.from = 0;
        first.to = step - 1;
        const second = {
          title: { en: '', el: '', it: '' },
          description: { en: '', el: '', it: '' },
          color: '#' + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0'),
          from: step,
          to: step * 2 - 1,
          expiration_days: this.tiers_settings.system === 'expiration_mode' ? -1 : undefined,
        };
        this.tierValues = [first, second];
      }

      this.tierPointChanged = true;
      this.$nextTick(() => {
        this.getTiersErrors();
        this.rerenderDOM();
      });
    },

    submitTierForm({ tier, i }) {
      const nextValues = cloneDeep(this.tierValues);
      const previousTier = nextValues[i] || {};

      const sanitizedTier = cloneDeep(tier);
      delete sanitizedTier.errors;

      const mergedTier = {
        ...previousTier,
        ...sanitizedTier,
        title: sanitizedTier.title ? cloneDeep(sanitizedTier.title) : cloneDeep(previousTier.title),
        description: sanitizedTier.description
          ? cloneDeep(sanitizedTier.description)
          : cloneDeep(previousTier.description),
      };

      mergedTier.key = mergedTier.title?.[this.lang] + mergedTier.color;

      if (i === nextValues.length - 1 && sanitizedTier.to === undefined) {
        nextValues[i].to = undefined;
      }
      nextValues[i] = { ...nextValues[i], ...mergedTier };
      this.tierValues = nextValues;

      this.cascadePullBackward(i);
      this.cascadePushForward(i);

      this.tierPointChanged = true;

      this.$set(this.error.title, i, '');
      this.$set(this.error.description, i, '');
      this.getTiersErrors();
      this.rerenderDOM();
    },

    handleChildValidation(payload) {
      const { type, i, tierData } = payload;
      if (type === 'title') {
        this.validateTitle({ i, tierData });
      } else if (type === 'description') {
        this.validateDescription({ i, tierData });
      } else if (type === 'expiration_days') {
        this.validateExpirationDays({ i, tierData });
      } else if (type === 'valid_days_before') {
        this.validateCountableDays({ i, tierData });
      }
      this.getTiersErrors();
    },

    handleClearError(payload) {
      const { type, i } = payload;
      if (!this.error[type]) return;
      this.$set(this.error[type], i, '');
    },

    handleErrorsUpdated(payload) {
      const { i, errors } = payload;
      console.debug('[PointTiers] handleErrorsUpdated', { i, errors });
      // apply to parent's error arrays
      Object.keys(errors).forEach((k) => {
        if (!this.error[k]) return;
        this.$set(this.error[k], i, errors[k] || '');
      });
      // update tierValues[i].errors for display in the form
      const tierErrs = Object.values(errors).filter((e) => e && String(e).trim());
      if (this.tierValues[i]) this.$set(this.tierValues[i], 'errors', tierErrs);
    },

    //Render the new tiers
    rerenderDOM() {
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    handleTierNumberChange() {
      if (this.tiersNumberChanging) return;
      this.tiersNumberChanging = true;
      const current = (this.tierValues && this.tierValues.length) || 0;
      const target = Number(this.currentTiersNumber) || 0;
      if (current > target) {
        for (let i = current; i > target; i--) {
          this.removeTier();
        }
      } else if (current < target) {
        for (let i = current; i < target; i++) {
          this.addTier();
        }
      }
      if (this._applyTierNumberChange && typeof this._applyTierNumberChange === 'function') {
        this._applyTierNumberChange();
      }
    },

    adjustPreviousTierMaximumAmountAndValidate(i, tier) {
      if (i > 0) {
        this.validateMinimumAmount(i, tier, false);

        this.$nextTick(() => {
          this.getTiersErrors();
          let prevTier = this.tierValues[i - 1];
          if (prevTier) {
            prevTier.to = tier.from - 1;
            try {
              const curFrom = Number(tier.from);
              const prevFrom = Number(prevTier.from);
              if (!Number.isNaN(curFrom) && !Number.isNaN(prevFrom) && curFrom <= prevFrom) {
                this.cascadePullBackward(i);
              }
            } catch (e) {
              this.setNotification({
                show: true,
                type: 'error',
                text:
                  this.translations?.tierValueParseError?.[this.lang] ||
                  'Error parsing tier values.',
              });
            }
          }
        });
      }
    },

    cascadePushForward(startIndex) {
      if (!Array.isArray(this.tierValues) || this.tierValues.length <= 1) return;
      const nextValues = cloneDeep(this.tierValues);
      const changedIndices = [];

      const toNumber = (v) =>
        v === null || typeof v === 'undefined' || v === '' ? undefined : Number(v);

      for (let idx = Math.max(1, startIndex); idx < nextValues.length; idx++) {
        const prev = nextValues[idx - 1];
        const cur = nextValues[idx];
        const prevFrom = toNumber(prev.from) || 0;
        const curFrom = toNumber(cur.from) || 0;
        if (curFrom <= prevFrom) {
          cur.from = prevFrom + 1;
          changedIndices.push(idx);
        }
      }

      for (let k = 0; k < nextValues.length; k++) {
        if (k < nextValues.length - 1) {
          const nextFrom = toNumber(nextValues[k + 1].from) || 0;
          nextValues[k].to = nextFrom - 1;
        } else {
          nextValues[k].to = undefined;
        }
      }

      if (changedIndices.length) {
        this.tierValues = nextValues;
        this.tierPointChanged = true;
        this.$nextTick(() => {
          this.getTiersErrors();
          this.rerenderDOM();
        });

        const msg = {
          en: `Also adjusted ${changedIndices.length} tier(s) forward to keep ranges consistent.`,
          el: `Επιπλέον προσαρμόστηκαν ${changedIndices.length} επίπεδα προς τα εμπρός για συνέπεια.`,
          it: `Regolati anche ${changedIndices.length} livelli in avanti per mantenere la coerenza.`,
        };
        this.setNotification({ show: true, type: 'info', text: msg[this.lang] || msg.en });
      }
    },

    cascadePullBackward(startIndex) {
      if (!Array.isArray(this.tierValues) || this.tierValues.length <= 1) return;
      const nextValues = cloneDeep(this.tierValues);
      const changedIndices = [];

      const toNumber = (v) =>
        v === null || typeof v === 'undefined' || v === '' ? undefined : Number(v);

      for (let idx = startIndex; idx > 0; idx--) {
        const cur = nextValues[idx];
        const prev = nextValues[idx - 1];
        const curFrom = toNumber(cur.from) || 0;
        const prevFrom = toNumber(prev.from) || 0;
        if (prevFrom >= curFrom) {
          prev.from = Math.max(0, curFrom - 1);
          changedIndices.push(idx - 1);
        }
      }

      for (let k = 0; k < nextValues.length; k++) {
        if (k < nextValues.length - 1) {
          const nextFrom = toNumber(nextValues[k + 1].from) || 0;
          nextValues[k].to = nextFrom - 1;
        } else {
          nextValues[k].to = undefined;
        }
      }

      if (changedIndices.length) {
        this.tierValues = nextValues;
        this.tierPointChanged = true;
        this.$nextTick(() => {
          this.getTiersErrors();
          this.rerenderDOM();
        });

        const msg = {
          en: `Also adjusted ${changedIndices.length} tier(s) backward to keep ranges consistent.`,
          el: `Επιπλέον προσαρμόστηκαν ${changedIndices.length} επίπεδα προς τα πίσω για συνέπεια.`,
          it: `Regolati anche ${changedIndices.length} livelli indietro per mantenere la coerenza.`,
        };
        this.setNotification({ show: true, type: 'info', text: msg[this.lang] || msg.en });
      }
    },

    getTextColor(hex) {
      const clean = hex.replace('#', '').slice(0, 6);
      const r = parseInt(clean.substring(0, 2), 16) / 255;
      const g = parseInt(clean.substring(2, 4), 16) / 255;
      const b = parseInt(clean.substring(4, 6), 16) / 255;
      const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
      const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
      return L > 0.179 ? '#000000' : '#ffffff';
    },

    getRelativeLuminance(hex) {
      if (!hex || typeof hex !== 'string') return 0;
      const normalized = hex.replace('#', '');
      const clean =
        normalized.length === 3
          ? normalized
              .split('')
              .map((c) => c + c)
              .join('')
          : normalized.slice(0, 6);
      if (clean.length !== 6) return 0;

      const r = parseInt(clean.substring(0, 2), 16) / 255;
      const g = parseInt(clean.substring(2, 4), 16) / 255;
      const b = parseInt(clean.substring(4, 6), 16) / 255;
      const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
      return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    },

    getConnectorBorderColor(color) {
      if (!color || typeof color !== 'string') return '#000000';
      // Use black border only for colors that are very close to white.
      const luminance = this.getRelativeLuminance(color);
      return luminance >= 0.82 ? '#000000' : color;
    },

    hexToRgba(hex, alpha) {
      const clean = hex.replace('#', '').slice(0, 6);
      const r = parseInt(clean.substring(0, 2), 16);
      const g = parseInt(clean.substring(2, 4), 16);
      const b = parseInt(clean.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    selectTier(index) {
      const el = this.$refs.wheelViewport;
      if (!el) return;
      this.sliderValue = index;
      el.scrollTo({ top: index * this.wheelItemHeight, behavior: 'smooth' });
    },

    wheelScroll(dir) {
      const next = Math.max(0, Math.min(this.sliderValue + dir, this.tierValues.length - 1));
      this.selectTier(next);
    },

    onWheel(e) {
      const el = this.$refs.wheelViewport;
      if (!el) return;
      const steps = Math.ceil(this.tierValues.length / 20);
      el.scrollBy({
        top: Math.sign(e.deltaY) * steps * this.wheelItemHeight,
        behavior: 'smooth',
      });
    },

    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const el = this.$refs.wheelViewport;
        if (!el) return;
        const idx = Math.round(el.scrollTop / this.wheelItemHeight);
        const clamped = Math.max(0, Math.min(idx, this.tierValues.length - 1));
        if (this.sliderValue !== clamped) {
          this.sliderValue = clamped;
          el.scrollTo({ top: clamped * this.wheelItemHeight, behavior: 'smooth' });
        }
      }, 120);
    },

    initializeWheel() {
      this.$nextTick(() => {
        const el = this.$refs.wheelViewport;
        if (el) el.scrollTop = this.sliderValue * this.wheelItemHeight;
      });
    },
  },

  watch: {
    tiers: {
      handler(val, oldVal) {
        this.getTierValues();
        if (JSON.stringify(val) === JSON.stringify(oldVal) || this.updatingTiers)
          this.updatingTiers = true;
        this.$nextTick(() => {
          if (
            !this.tiersAreNewlyCreated &&
            (!this.savedTiers.length || JSON.stringify(val) === JSON.stringify(this.savedTiers))
          ) {
            this.tierPointChanged = false;
          } else {
            if (this.savedTiers.length !== val.length) {
              this.tierPointChanged = true;
            }
            val.forEach((e, i) => {
              if (
                !this.savedTiers[i] ||
                e.from !== this.savedTiers[i].from ||
                e.to !== this.savedTiers[i].to
              ) {
                this.tierPointChanged = true;
              }
            });
          }
          this.updatingTiers = false;
        });
      },
      deep: true,
    },

    ['tierValues.length']: {
      async handler(val, oldVal) {
        if (val) {
          if (val != this.currentTiersNumber) {
            this.currentTiersNumber = val;
          }
          await this.$nextTick(async () => {
            this.getTiersErrors();
          });
        }
      },
    },
  },
};
</script>

<style scoped>
.tiers-main-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0;
}

.tiers-layout {
  position: relative;
  padding-top: 28px;
}

.tiers-number-floating {
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 15;
}

.wheel-number-container {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  margin-top: 0;
  z-index: 20;
}

.wheel-with-connectors {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.wheel-container {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: auto;
  position: sticky;
  top: 96px;
}

.wheel-arrow {
  flex-shrink: 0;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.5);
  transition: color 0.15s;
}
.wheel-arrow:hover:not(:disabled) {
  color: rgba(0, 0, 0, 0.85);
}
.wheel-arrow:disabled {
  opacity: 0.25;
  cursor: default;
}

.wheel-track-wrap {
  position: relative;
  overflow: hidden;
  height: 570px;
}

.wheel-track {
  position: absolute;
  inset: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-width: none;
}
.wheel-track::-webkit-scrollbar {
  display: none;
}

.wheel-pad {
  height: calc(50% - 75px);
}

.wheel-item {
  height: 150px;
  --wheel-item-notch-depth: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 28px 12px;
  transition:
    opacity 0.15s,
    filter 0.15s,
    transform 0.15s;
  filter: brightness(0.9);
  position: relative;
  margin: 0;
  border-radius: 0px;
}

.wheel-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--wheel-item-bg, transparent);
  clip-path: polygon(0 0, 50% var(--wheel-item-notch-depth), 100% 0, 100% 100%, 0 100%);
  box-shadow:
    inset 0 1px 0 var(--wheel-item-border-color, transparent),
    inset 1px 0 0 var(--wheel-item-border-color, transparent),
    inset -1px 0 0 var(--wheel-item-border-color, transparent);
  z-index: 0;
  pointer-events: none;
}

.wheel-item.is-first::before {
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
}

.wheel-item:hover {
  opacity: 0.75;
  filter: brightness(1.05);
  transform: none;
}
.wheel-item.is-selected {
  opacity: 1;
  filter: brightness(1.08) saturate(1.1);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  transform: none;
}

.wheel-item.is-selected .wheel-item-name {
  font-size: 1rem;
}

.wheel-item.is-selected .wheel-item-from {
  font-size: 0.82rem;
}

.wheel-item-name {
  font-size: 0.9rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: none;
  max-width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
}
.wheel-item-from {
  font-size: 0.75rem;
  margin-top: 6px;
  text-align: center;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

.wheel-item-end-shape {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -18px;
  width: 100%;
  height: 18px;
  clip-path: polygon(0 0, 100% 0, 50% 100%);
  z-index: 0;
  pointer-events: none;
  filter: drop-shadow(0 1px 0 var(--wheel-item-border-color, transparent))
    drop-shadow(1px 0 0 var(--wheel-item-border-color, transparent))
    drop-shadow(-1px 0 0 var(--wheel-item-border-color, transparent));
}

.tier-connector-arrow {
  flex-shrink: 0;
  width: 0;
  height: 0;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
  transition:
    border-left-color 0.22s ease,
    border-right-color 0.22s ease;
  z-index: 10;
}

.tier-connector-arrow--left {
  border-left: 22px solid;
  filter: drop-shadow(1px 0 0 var(--connector-border-color, #000))
    drop-shadow(-1px 0 0 var(--connector-border-color, #000))
    drop-shadow(0 1px 0 var(--connector-border-color, #000))
    drop-shadow(0 -1px 0 var(--connector-border-color, #000))
    drop-shadow(2px 0 4px rgba(0, 0, 0, 0.18));
}

.tier-connector-arrow--right {
  border-right: 22px solid;
  filter: drop-shadow(1px 0 0 var(--connector-border-color, #000))
    drop-shadow(-1px 0 0 var(--connector-border-color, #000))
    drop-shadow(0 1px 0 var(--connector-border-color, #000))
    drop-shadow(0 -1px 0 var(--connector-border-color, #000))
    drop-shadow(-2px 0 4px rgba(0, 0, 0, 0.18));
}

.wheel-fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 28%;
  pointer-events: none;
  z-index: 1;
}
.wheel-fade--top {
  top: 0;
  background: linear-gradient(to bottom, rgba(245, 245, 245, 0.82), transparent);
}
.wheel-fade--bottom {
  bottom: 0;
  background: linear-gradient(to top, rgba(245, 245, 245, 0.82), transparent);
}

.__placeholder {
  display: none;
}

.tier-form-panel {
  flex: 0 1 620px;
  max-width: 720px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-left: none;
  border-radius: 0 6px 6px 0;
  margin-left: 0;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease;
  position: relative;
  margin-top: 32px;
}

.tier-indicator-container {
  position: absolute;
  right: -18px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tier-indicator {
  overflow: hidden;
  margin-left: 50%;
}

.tier-indicator .tier-indicator-icon {
  display: inline-block;
  margin-left: -36px;
  display: none;
}

@media (max-width: 900px) {
  .wheel-with-connectors {
    width: 100%;
  }

  .wheel-container {
    position: sticky;
    left: auto;
    top: 0;
    width: 180px;
  }
  .wheel-track-wrap {
    height: 450px;
  }
  .wheel-number-container {
    margin-top: -80px;
  }
  .wheel-item {
    height: 150px;
    margin: 0;
    padding: 12px 18px 32px;
  }
  .wheel-item-end-shape {
    height: 24px;
  }
  .wheel-pad {
    height: calc(50% - 75px);
  }
}
</style>
